import { api } from "../api";

export interface Staff {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  staffId?: string | null;
  profilePicture?: string | null;
  branchId?: string | null;
  roleId?: string | null;
  departmentId: string;
  jobTitleId: string;
  status: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED";
  profileMetadata?: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  branch?: {
    id: string;
    name: string;
  } | null;
  role?: {
    id: string;
    name: string;
  } | null;
  department?: {
    id: string;
    name: string;
  } | null;
  jobTitle?: {
    id: string;
    name: string;
  } | null;
}

export interface CreateStaffDto {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  staffId?: string;
  roleId: string;
  branchId?: string;
  departmentId: string;
  jobTitleId: string;
  customFields?: Record<string, any>;
}

export interface UpdateStaffDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  staffId?: string;
  roleId?: string;
  branchId?: string | null;
  departmentId?: string;
  jobTitleId?: string;
  customFields?: Record<string, any>;
}

export interface UpdateStaffProfileDto {
  profilePicture?: string;
  profileMetadata?: Record<string, any>;
}

export interface AcceptInviteDto {
  token: string;
  password: string;
}

export interface StaffListParams {
  branchId?: string;
  roleId?: string;
  departmentId?: string;
  status?: string;
}

export const staffApi = {
  getAll: async (params?: StaffListParams): Promise<Staff[]> => {
    const response = await api.get("/staff", { params });
    return response.data;
  },

  getById: async (id: string): Promise<Staff> => {
    const response = await api.get(`/staff/${id}`);
    return response.data;
  },

  create: async (data: CreateStaffDto, reAuthPassword?: string): Promise<Staff> => {
    const response = await api.post("/staff", data, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
    return response.data;
  },

  update: async (id: string, data: UpdateStaffDto, reAuthPassword?: string): Promise<Staff> => {
    const response = await api.patch(`/staff/${id}`, data, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
    return response.data;
  },

  updateProfile: async (id: string, data: UpdateStaffProfileDto): Promise<Staff> => {
    const response = await api.patch(`/staff/${id}/profile`, data);
    return response.data;
  },

  resendInvite: async (id: string): Promise<void> => {
    await api.post(`/staff/${id}/invite/resend`);
  },

  revokeInvite: async (id: string): Promise<void> => {
    await api.post(`/staff/${id}/invite/revoke`);
  },

  activate: async (id: string, reAuthPassword?: string): Promise<Staff> => {
    const response = await api.post(
      `/staff/${id}/activate`,
      {},
      {
        headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
      }
    );
    return response.data;
  },

  deactivate: async (id: string, reAuthPassword?: string): Promise<Staff> => {
    const response = await api.post(
      `/staff/${id}/deactivate`,
      {},
      {
        headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
      }
    );
    return response.data;
  },

  suspend: async (id: string, reAuthPassword?: string): Promise<Staff> => {
    const response = await api.post(
      `/staff/${id}/suspend`,
      {},
      {
        headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
      }
    );
    return response.data;
  },

  deleteStaff: async (id: string, reAuthPassword?: string): Promise<{ message: string }> => {
    const response = await api.delete(`/staff/${id}`, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
    return response.data;
  },

  getPermissions: async (id: string): Promise<string[]> => {
    const response = await api.get(`/staff/${id}/permissions`);
    return response.data.permissions || [];
  },

  validateInviteToken: async (
    token: string
  ): Promise<{
    firstName: string;
    lastName: string;
    libraryName: string;
    tenantSlug: string;
    tenantDomains: string[];
  }> => {
    const response = await api.get("/staff/validate-invite", { params: { token } });
    return response.data;
  },

  acceptInvite: async (
    data: AcceptInviteDto
  ): Promise<Staff & { tenant?: { name: string; slug: string; domains: string[] } }> => {
    const response = await api.post("/staff/accept-invite", data);
    return response.data;
  },

  /**
   * Export staff as CSV and trigger download
   */
  exportCsv: async (): Promise<void> => {
    const response = await api.get("/staff/export", {
      responseType: "blob",
      headers: { Accept: "text/csv" },
    });
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Download CSV template for staff import
   */
  downloadTemplate: async (): Promise<void> => {
    const response = await api.get("/staff/export/template", {
      responseType: "blob",
      headers: { Accept: "text/csv" },
    });
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "staff-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  },

  /**
   * Import staff from CSV file. Requires re-auth password.
   */
  importCsv: async (
    file: File,
    reAuthPassword: string
  ): Promise<{ created: number; failed: Array<{ row: number; email: string; error: string }> }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/staff/import", formData, {
      headers: {
        "X-Re-Auth-Password": reAuthPassword,
      },
    });
    return response.data;
  },
};
